import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    ShieldCheck,
    Compass,
    HeartHandshake,
    Globe2,
    Workflow,
    Handshake,
    User,
} from "lucide-react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import Globe from "../components/site/Globe";
import { FadeUp, SectionHead, ChapterTag, MaskLine } from "../components/site/Reveal";
import { scrollToId } from "../lib/scroll";
import { COMPANY_DETAILS, ABOUT_NETWORK, VALUES, TEAM_GROUPS } from "../data/content";

const VALUE_ICONS = [ShieldCheck, Compass, HeartHandshake, Globe2, Workflow, Handshake];

export default function About() {
    const navigate = useNavigate();
    const goContact = () => {
        navigate("/");
        setTimeout(() => scrollToId("#contact"), 500);
    };

    return (
        <div className="grain bg-navy-deep" data-testid="about-page">
            <Header />
            <main>
                <section className="relative overflow-hidden bg-navy-deep pt-[76px]" data-testid="about-hero">
                    <div className="hero-grid pointer-events-none absolute inset-0" aria-hidden="true" />
                    <div
                        className="pointer-events-none absolute -left-40 top-0 h-[480px] w-[480px] rounded-full"
                        style={{ background: "radial-gradient(circle, rgba(197,160,89,0.1) 0%, transparent 65%)" }}
                        aria-hidden="true"
                    />
                    <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 items-center gap-12 px-5 py-16 sm:py-20 lg:grid-cols-12 lg:px-10 lg:py-24">
                        <div className="lg:col-span-7">
                            <FadeUp>
                                <div className="flex items-center gap-4">
                                    <span className="h-px w-12 bg-gold" aria-hidden="true" />
                                    <span className="text-[11px] font-semibold uppercase tracking-[0.34em] text-gold sm:text-xs">
                                        About Viet Medica Ltd
                                    </span>
                                </div>
                            </FadeUp>
                            <h1 className="mt-8 font-serif text-4xl font-light leading-[1.05] tracking-tight text-ivory sm:text-5xl lg:text-6xl xl:text-7xl">
                                <MaskLine delay={0.2}>Global Connections</MaskLine>
                                <MaskLine delay={0.38}>
                                    <span>
                                        Built on <em className="font-medium italic text-gold">Trust.</em>
                                    </span>
                                </MaskLine>
                            </h1>
                            <FadeUp delay={0.55}>
                                <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-300/90 md:text-lg">
                                    Viet Medica Ltd is a Mauritius-registered international business group
                                    connecting people, healthcare providers, hospitals, buyers, suppliers and
                                    strategic partners across global markets.
                                </p>
                                <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-300/80 md:text-lg">
                                    From our corporate base in Mauritius, we work through an expanding
                                    international network across Asia, Africa, the Middle East and Europe. Our
                                    activities are organised through two specialist divisions: Healthcare &amp;
                                    Medical Tourism and International Trading.
                                </p>
                            </FadeUp>
                        </div>
                        <div className="hidden lg:col-span-5 lg:block">
                            <FadeUp delay={0.3}>
                                <Globe className="mx-auto h-auto w-full max-w-[440px] opacity-90" />
                            </FadeUp>
                        </div>
                    </div>
                </section>

                <section className="border-y border-gold/15 bg-navy-surface py-20 lg:py-28" data-testid="about-purpose">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <ChapterTag>Our Purpose</ChapterTag>
                        <FadeUp delay={0.1}>
                            <p className="mt-8 max-w-4xl font-serif text-2xl font-light leading-snug text-ivory sm:text-3xl lg:text-[2.5rem] lg:leading-[1.3]">
                                &ldquo;Our purpose is to make international healthcare, travel, partnerships and
                                trade <em className="font-medium italic text-gold">easier to understand and coordinate</em>.
                                We bring together suitable people and organisations, support clear communication
                                and help each opportunity move forward professionally.&rdquo;
                            </p>
                        </FadeUp>
                    </div>
                </section>

                <section className="bg-ivory py-24 text-navy-deep lg:py-32" data-testid="about-corporate-base">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-2 lg:gap-20">
                            <div>
                                <SectionHead light eyebrow="Mauritius" title="Our Corporate Base" />
                                <FadeUp delay={0.1}>
                                    <p className="mt-6 text-base leading-relaxed text-navy-deep/75 md:text-lg">
                                        Viet Medica Ltd is registered in Mauritius, a multicultural and
                                        internationally connected island nation positioned between Africa and Asia.
                                    </p>
                                    <p className="mt-5 text-base leading-relaxed text-navy-deep/75 md:text-lg">
                                        Mauritius reflects the character of our group: globally connected,
                                        commercially outward-looking and experienced in working across cultures. It
                                        provides the corporate foundation for our international healthcare,
                                        medical-tourism, travel and trading activities.
                                    </p>
                                </FadeUp>
                            </div>

                            <FadeUp delay={0.15}>
                                <div className="border border-navy-deep/10 bg-white p-8 lg:p-10" data-testid="company-details-card">
                                    <span className="text-[11px] font-semibold uppercase tracking-[0.3em] text-gold">
                                        Company Details
                                    </span>
                                    <div className="mt-6">
                                        {COMPANY_DETAILS.map((d) => (
                                            <div
                                                key={d.id}
                                                data-testid={`company-field-${d.id}`}
                                                className="flex flex-col gap-1 border-t border-navy-deep/10 py-4 sm:flex-row sm:items-center sm:justify-between"
                                            >
                                                <span className="text-sm font-semibold text-navy-deep/85">{d.label}</span>
                                                {d.value ? (
                                                    <span className="text-sm text-navy-deep/80 sm:text-right">{d.value}</span>
                                                ) : (
                                                    <span className="text-[11px] uppercase tracking-[0.22em] text-navy-deep/40">
                                                        To be confirmed
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                        <div className="border-t border-navy-deep/10" aria-hidden="true" />
                                    </div>
                                    <p className="mt-6 text-xs leading-relaxed text-navy-deep/50">
                                        Viet Medica Ltd is registered in Mauritius. For corporate enquiries, use
                                        the details above or the enquiry forms on this website.
                                    </p>
                                </div>
                            </FadeUp>
                        </div>
                    </div>
                </section>

                <section className="bg-navy-deep py-24 lg:py-32" data-testid="about-network">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <div className="grid grid-cols-1 gap-14 lg:grid-cols-2 lg:gap-20">
                            <div>
                                <SectionHead
                                    eyebrow="Our Global Network"
                                    title="Local Knowledge. One Reliable Point of Contact."
                                    copy="Our teams, representatives and partners work across different countries, languages and time zones. This allows us to combine local knowledge with international business experience while giving clients a clear and reliable point of contact."
                                />
                            </div>
                            <div>
                                {ABOUT_NETWORK.map((r, i) => (
                                    <FadeUp key={r.name} delay={i * 0.06}>
                                        <div
                                            className="flex flex-col gap-1 border-t border-white/10 py-5 transition-colors duration-500 hover:border-gold/30 sm:flex-row sm:items-baseline sm:justify-between"
                                            data-testid={`about-region-${r.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        >
                                            <span className="font-serif text-xl font-normal text-ivory lg:text-2xl">{r.name}</span>
                                            <span className="text-xs uppercase tracking-[0.18em] text-gold/80">{r.role}</span>
                                        </div>
                                    </FadeUp>
                                ))}
                                <div className="border-t border-white/10" aria-hidden="true" />
                                <FadeUp delay={0.2}>
                                    <p className="mt-6 text-xs leading-relaxed tracking-wide text-slate-500">
                                        We work through teams, representatives and partners, and do not claim
                                        physical offices in markets where an office has not been confirmed.
                                    </p>
                                </FadeUp>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="bg-navy-surface py-24 lg:py-32" data-testid="about-values">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <ChapterTag>What Guides Us</ChapterTag>
                        <SectionHead className="mt-8" eyebrow="Our Values" title="The Principles Behind Every Connection" />
                        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {VALUES.map((v, i) => {
                                const Icon = VALUE_ICONS[i];
                                return (
                                    <FadeUp key={v.title} delay={(i % 3) * 0.08} className="h-full">
                                        <div
                                            className="group flex h-full flex-col border border-white/8 bg-navy-deep p-8 transition-colors duration-500 hover:border-gold/40"
                                            data-testid={`value-card-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        >
                                            <Icon size={22} className="text-gold transition-transform duration-500 group-hover:-translate-y-1" aria-hidden="true" />
                                            <h3 className="mt-6 font-serif text-2xl font-normal text-ivory">{v.title}</h3>
                                            <p className="mt-3 text-sm leading-relaxed text-slate-300/80">{v.copy}</p>
                                        </div>
                                    </FadeUp>
                                );
                            })}
                        </div>
                    </div>
                </section>

                <section className="bg-navy-deep py-24 lg:py-32" data-testid="about-team">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <ChapterTag>Our Leadership and Team</ChapterTag>
                        <SectionHead
                            className="mt-8"
                            eyebrow="The Group"
                            title="The People Behind Viet Medica"
                            copy="Genuine profiles are published once confirmed by Viet Medica Ltd. No names, photographs, qualifications or biographies are shown until officially supplied."
                        />
                        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                            {TEAM_GROUPS.map((t, i) => (
                                <FadeUp key={t} delay={(i % 3) * 0.08} className="h-full">
                                    <div
                                        className="flex h-full flex-col border border-dashed border-white/15 bg-navy-surface/50 p-8"
                                        data-testid={`team-slot-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    >
                                        <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40" aria-hidden="true">
                                            <User size={20} className="text-gold/80" />
                                        </span>
                                        <h3 className="mt-6 font-serif text-xl font-normal text-ivory lg:text-2xl">{t}</h3>
                                        <span className="mt-3 text-[10px] font-semibold uppercase tracking-[0.28em] text-gold/60">
                                            Profile to be confirmed
                                        </span>
                                    </div>
                                </FadeUp>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="bg-navy-deep pb-24 lg:pb-32" data-testid="about-cta">
                    <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
                        <FadeUp>
                            <div className="flex flex-col items-start gap-8 border border-gold/25 bg-gradient-to-b from-navy-surface to-navy-deep p-10 lg:flex-row lg:items-center lg:justify-between lg:p-14">
                                <h2 className="max-w-xl font-serif text-3xl font-light leading-tight text-ivory lg:text-4xl">
                                    Speak with the Viet Medica team.
                                </h2>
                                <button
                                    type="button"
                                    data-testid="about-cta-button"
                                    onClick={goContact}
                                    className="group inline-flex shrink-0 items-center gap-3 bg-gold px-7 py-4 text-xs font-bold uppercase tracking-[0.18em] text-navy-deep transition-colors duration-300 hover:bg-gold-bright"
                                >
                                    Speak to Our Team
                                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </button>
                            </div>
                        </FadeUp>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
